import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import { useDownloadTerminalServiceDocument } from "@features/terminal-services/hooks/useDownloadTerminalServiceDocument";
import type { BypassServiceDetailsData } from "@services/terminal-services/types";
import { useTranslation } from "react-i18next";
import type { TerminalServiceDetailsViewProps } from "../types";
import { formatTerminalServiceDateTime } from "../utils";
import DocumentCard from "./DocumentCard";

const SERVICE = "bff-terminal-service";
const TEST_ID_BASE = "bypass-service-details";
const LANG_BASE = "terminal-services.details";
const EMPTY_VALUE = "-";

const detailLabelClassName = "w-[52%] shrink-0";
const detailValueClassName = "w-[48%]";
const detailLabelTextClassName = "font-normal text-[#646A77]";
const detailValueTextClassName = "font-normal text-[#31343B]";
const detailRowClassName = "border-b border-[#F1F2F4] pb-3";

const valueOrEmpty = (value?: string | number | null) => {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  const normalized = String(value).trim();

  return normalized || EMPTY_VALUE;
};
interface DetailItem {
  testId: string;
  label: string;
  value: string;
}

const DetailItemRow = ({ testId, label, value, isLast }: DetailItem & { isLast?: boolean }) => (
  <PaperItem
    testId={testId}
    translate={label}
    text={value}
    wrapperStyle={isLast ? "" : detailRowClassName}
    tranStyle={detailLabelTextClassName}
    textStyles={detailValueTextClassName}
    labelClassName={detailLabelClassName}
    valueClassName={detailValueClassName}
  />
);

const BypassServiceDetails = ({
  serviceType,
  terminalServiceDetails,
}: TerminalServiceDetailsViewProps) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadTerminalServiceDocument();

  const details = terminalServiceDetails?.serviceDetails as BypassServiceDetailsData | undefined;
  const serviceTypeLabel = t(`${LANG_BASE}.service-type.${serviceType}`);
  const attachmentDocuments = details?.attachments ?? [];
  const closureDocument =
    details?.closureDetails?.attachment ??
    details?.closureAttachment ??
    details?.closureDocument ??
    details?.taskDecision?.closureAttachment ??
    null;
  const closureRemarks = details?.closureDetails?.remarks ?? null;
  const closureDate = details?.closureDetails?.createdAt ?? null;
  const closedBy = details?.closureDetails?.createdBy ?? null;
  const shouldShowClosureDetails =
    details?.status === "CLOSED" || Boolean(closureDate || closedBy || closureRemarks);

  const booleanOrEmpty = (value?: boolean | null) => {
    if (typeof value !== "boolean") {
      return EMPTY_VALUE;
    }

    return t(value ? "common.yes" : "common.no");
  };

  const leftColumnItems: DetailItem[] = [
    {
      testId: `${TEST_ID_BASE}-service-type`,
      label: t(`${LANG_BASE}.fields.service-type`),
      value: serviceTypeLabel,
    },
    {
      testId: `${TEST_ID_BASE}-date-time`,
      label: t(`${LANG_BASE}.fields.date-time`),
      value: formatTerminalServiceDateTime(details?.sourceDateTime),
    },
    {
      testId: `${TEST_ID_BASE}-vessel-imo`,
      label: t(`${LANG_BASE}.fields.vessel-imo`),
      value: valueOrEmpty(details?.vesselImo),
    },
    {
      testId: `${TEST_ID_BASE}-operation-type`,
      label: t(`${LANG_BASE}.fields.operation-type`),
      value: valueOrEmpty(details?.operationType),
    },
    {
      testId: `${TEST_ID_BASE}-plta`,
      label: t(`${LANG_BASE}.fields.plta`),
      value: valueOrEmpty(details?.plta),
    },
    {
      testId: `${TEST_ID_BASE}-mov-number`,
      label: t(`${LANG_BASE}.fields.mov-number`),
      value: valueOrEmpty(details?.movNumber),
    },
    {
      testId: `${TEST_ID_BASE}-reason-for-bypass`,
      label: t(`${LANG_BASE}.fields.reason-for-bypass`),
      value: valueOrEmpty(details?.reasonForBypass),
    },
    {
      testId: `${TEST_ID_BASE}-expected-closure-date`,
      label: t(`${LANG_BASE}.fields.expected-closure-date`),
      value: formatTerminalServiceDateTime(details?.expectedClosureDate),
    },
  ];

  const rightColumnItems: DetailItem[] = [
    {
      testId: `${TEST_ID_BASE}-vessel-name`,
      label: t(`${LANG_BASE}.fields.vessel-name`),
      value: valueOrEmpty(details?.vesselName),
    },
    {
      testId: `${TEST_ID_BASE}-location`,
      label: t(`${LANG_BASE}.fields.location`),
      value: valueOrEmpty(details?.location),
    },
    {
      testId: `${TEST_ID_BASE}-terminal`,
      label: t(`${LANG_BASE}.fields.terminal`),
      value: valueOrEmpty(details?.customerTerminal),
    },
    {
      testId: `${TEST_ID_BASE}-dock-line`,
      label: t(`${LANG_BASE}.fields.dock-line`),
      value: valueOrEmpty(details?.dockLine),
    },
    {
      testId: `${TEST_ID_BASE}-plda-number`,
      label: t(`${LANG_BASE}.fields.plda-number`),
      value: valueOrEmpty(details?.pldaNumber),
    },
    {
      testId: `${TEST_ID_BASE}-remarks`,
      label: t(`${LANG_BASE}.fields.remarks`),
      value: valueOrEmpty(details?.remarks),
    },
  ];

  const mitigationLeftColumnItems: DetailItem[] = [
    {
      testId: `${TEST_ID_BASE}-physical-verification-of-valve-tag`,
      label: t(`${LANG_BASE}.fields.physical-verification-of-valve-tag`),
      value: booleanOrEmpty(details?.physicalVerificationOfValveTag),
    },
    {
      testId: `${TEST_ID_BASE}-removal-of-padlock-witnessed-by-fott-operator`,
      label: t(`${LANG_BASE}.fields.removal-of-padlock-witnessed-by-fott-operator`),
      value: booleanOrEmpty(details?.removalOfPadlockWitnessedByFottOperator),
    },
    {
      testId: `${TEST_ID_BASE}-confirmation-of-instructions-with-customer-ccr`,
      label: t(`${LANG_BASE}.fields.confirmation-of-instructions-with-customer-ccr`),
      value: booleanOrEmpty(details?.confirmationOfInstructionsWithCustomerCcr),
    },
  ];

  const mitigationRightColumnItems: DetailItem[] = [
    {
      testId: `${TEST_ID_BASE}-collection-of-valve-key-from-fott-ccr`,
      label: t(`${LANG_BASE}.fields.collection-of-valve-key-from-fott-ccr`),
      value: booleanOrEmpty(details?.collectionOfValveKeyFromFottCcr),
    },
    {
      testId: `${TEST_ID_BASE}-cross-check-communication`,
      label: t(`${LANG_BASE}.fields.cross-check-communication`),
      value: booleanOrEmpty(details?.crossCheckCommunication),
    },
    {
      testId: `${TEST_ID_BASE}-confirmation-of-instructions-with-fott-ccr`,
      label: t(`${LANG_BASE}.fields.confirmation-of-instructions-with-fott-ccr`),
      value: booleanOrEmpty(details?.confirmationOfInstructionsWithFottCcr),
    },
  ];

  const closureLeftColumnItems: DetailItem[] = [
    {
      testId: `${TEST_ID_BASE}-closure-date`,
      label: t(`${LANG_BASE}.fields.closure-date`),
      value: formatTerminalServiceDateTime(closureDate),
    },
    {
      testId: `${TEST_ID_BASE}-closure-remarks`,
      label: t(`${LANG_BASE}.fields.closure-remarks`),
      value: valueOrEmpty(closureRemarks),
    },
  ];

  const closureRightColumnItems: DetailItem[] = [
    {
      testId: `${TEST_ID_BASE}-closed-by`,
      label: t(`${LANG_BASE}.fields.closed-by`),
      value: valueOrEmpty(closedBy),
    },
  ];

  return (
    <div data-testid={TEST_ID_BASE} className="flex flex-col gap-8">
      <Paper variant="outlined">
        <p className="mb-4 font-bold text-[#31343B]">{t(`${LANG_BASE}.service-details-title`)}</p>

        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          <div>
            {leftColumnItems.map((item, index) => (
              <DetailItemRow
                key={item.testId}
                {...item}
                isLast={index === leftColumnItems.length - 1}
              />
            ))}
          </div>

          <div className="md:pt-[47px]">
            {rightColumnItems.map((item, index) => (
              <DetailItemRow
                key={item.testId}
                {...item}
                isLast={index === rightColumnItems.length - 1}
              />
            ))}
          </div>
        </div>
      </Paper>

      <Paper variant="outlined">
        <p className="mb-4 font-bold text-[#31343B]">
          {t(`${LANG_BASE}.mitigation-actions-title`)}
        </p>

        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          <div>
            {mitigationLeftColumnItems.map((item, index) => (
              <DetailItemRow
                key={item.testId}
                {...item}
                isLast={index === mitigationLeftColumnItems.length - 1}
              />
            ))}
          </div>

          <div>
            {mitigationRightColumnItems.map((item, index) => (
              <DetailItemRow
                key={item.testId}
                {...item}
                isLast={index === mitigationRightColumnItems.length - 1}
              />
            ))}
          </div>
        </div>
      </Paper>

      {shouldShowClosureDetails && (
        <Paper variant="outlined">
          <p className="mb-4 font-bold text-[#31343B]">
            {t(`${LANG_BASE}.service-closure-details-title`)}
          </p>

          <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            <div>
              {closureLeftColumnItems.map((item, index) => (
                <DetailItemRow
                  key={item.testId}
                  {...item}
                  isLast={index === closureLeftColumnItems.length - 1}
                />
              ))}
            </div>

            <div>
              {closureRightColumnItems.map((item, index) => (
                <DetailItemRow
                  key={item.testId}
                  {...item}
                  isLast={index === closureRightColumnItems.length - 1}
                />
              ))}
            </div>
          </div>
        </Paper>
      )}

      {(attachmentDocuments.length > 0 || closureDocument?.documentUrl) && (
        <section>
          <h2 className="mb-4 text-base font-normal text-[#646A77]">
            {t(`${LANG_BASE}.documents-title`)}
          </h2>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {attachmentDocuments.map((document, index) => (
              <DocumentCard
                key={`${document.documentUrl}-${index}`}
                document={document}
                testIdBase={`${TEST_ID_BASE}-attachment-${index + 1}`}
                title={document.documentName}
                showDocumentMetadata
                onPreview={() => handlePreview(SERVICE, document.documentUrl)}
                onDownload={() =>
                  handleDownload(SERVICE, document.documentUrl, document.documentName)
                }
              />
            ))}
            {closureDocument?.documentUrl && (
              <DocumentCard
                document={closureDocument}
                testIdBase={`${TEST_ID_BASE}-closure-document`}
                title={" "}
                showDocumentMetadata
                onPreview={() => handlePreview(SERVICE, closureDocument.documentUrl)}
                onDownload={() =>
                  handleDownload(SERVICE, closureDocument.documentUrl, closureDocument.documentName)
                }
              />
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default BypassServiceDetails;
